import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { access } from '@util/route/access';
import { DWS } from '@interfaces/dws';
import { AdminLoginAsWineryService, ImpersonationAccountRow } from "@services/dws/admin-login-as-winery.service";
import { observe } from "@web/util/loading/loading";
import { AccountsService } from '@services/accounts.service';
import { AuthenticateAdminService } from '@services/dws';
import { toast } from "@web/util/toast";
import { tr } from "@util/tr";
import { confirm } from "@web/app/components/dialogs/confirm";
import { WineryWineriesUtilService } from '@web/app/modules/winery-wineries-util/winery-wineries-util.service';
import { SidenavContentService } from '@web/app/services/sidenav-content.service';
import { WineriesService } from '@services/dws/wineries.service';
import { LandingRouteService } from '@services/permissions/landing-route.service';


@Component({
  selector: 'web-winery-import-list',
  templateUrl: './login-as-winery.component.html',
  styleUrls: ['./login-as-winery.component.scss'],
})

export class LoginAsWineryComponent implements OnInit {
  selectedWinery!: DWS.Winery;
  selectedWineryName: string = '';
  wineries: DWS.Winery[] = [];
  filteredWineries: DWS.Winery[] = [];
  loadingWineries: boolean = false;
  wineriesError: string = '';
  searchTerm: string = '';
  impersonationAccounts: ImpersonationAccountRow[] = [];
  selectedAccount: ImpersonationAccountRow | null = null;
  loadingAccounts: boolean = false;
  accountsError: string = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private accountsService: AccountsService,
    private adminLoginAsWineryService: AdminLoginAsWineryService,
    private adminAccountService: AuthenticateAdminService,
    private route: ActivatedRoute,
    private wineryWineriesUtilService: WineryWineriesUtilService,
    private sidenavContentService: SidenavContentService,
    private wineriesService: WineriesService,
    private landingRoute: LandingRouteService
  ) {
    // Try to get wineries from route data first (when loaded via routing)
    try {
      this.wineries = access(this.route).data('wineries') || [];
    } catch (e) {
      // If no route data available (when loaded dynamically), initialize empty array
      this.wineries = [];
    }
  }

  ngOnInit() {
    if (this.wineries.length > 0) {
      this.filteredWineries = [...this.wineries];
    } else {
      this.loadWineries();
    }

    setTimeout(() => {
      this.adjustOverlayWidth();
    }, 100);
  }

  private adjustOverlayWidth() {
    const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
    overlayPanes.forEach(pane => {
      const autocompletePanel = pane.querySelector('.mat-autocomplete-panel');
      if (autocompletePanel) {
        const inputElement = document.querySelector('.winery-select-field input');
        if (inputElement) {
          const inputWidth = inputElement.getBoundingClientRect().width;
          (autocompletePanel as HTMLElement).style.minWidth = `${inputWidth + 50}px`;
          (autocompletePanel as HTMLElement).style.width = `${inputWidth + 50}px`;
        }
      }
    });
  }

  private loadWineries() {
    this.loadingWineries = true;
    this.wineriesError = '';

    const privilegedToken = this.adminLoginAsWineryService.getOriginalAdminToken();
    if (!privilegedToken) {
      this.loadingWineries = false;
      this.wineriesError =
        'Sessione privilegiata non disponibile. Esci e rientra con la modalità Super Admin attiva.';
      return;
    }

    this.loadWineriesWithPrivilegedToken(privilegedToken);
  }

  private loadWineriesWithPrivilegedToken(privilegedToken: string) {
    // Use the WineriesService with admin token to make environment-aware API call
    this.wineriesService.listWithToken({}, privilegedToken, 0, 999999)
      .subscribe(
        (response) => {
          this.wineries = response.content || [];
          this.filteredWineries = [...(response.content || [])];
          this.loadingWineries = false;
        },
        (error) => {
          console.error('Error loading wineries with admin token:', error);
          this.wineries = [];
          this.loadingWineries = false;
          this.wineriesError = 'Errore nel caricamento delle cantine.';
        }
      );
  }

  retryLoadWineries() {
    this.loadWineries();
  }

  goToLogin() {
    void this.router.navigate(['/login']);
  }

  onWinerySelected(winery: DWS.Winery) {
    this.selectedWinery = winery;
    this.selectedWineryName = winery ? (winery.name || '') : '';
  }

  displayWinery = (winery: DWS.Winery) => {
    return winery ? winery.name : '';
  };

  onSearchChange() {
    this.searchTerm = this.selectedWineryName;
    this.filterWineries();
  }

  onInputClick() {
    // Ensure filteredWineries is initialized when clicking on input
    if (this.filteredWineries.length === 0 && this.wineries.length > 0) {
      this.filteredWineries = [...this.wineries];
    }
  }

  onWineryOptionSelected(winery: DWS.Winery) {
    this.selectedWinery = winery;
    this.selectedWineryName = winery.name || '';
    this.selectedAccount = null;
    this.impersonationAccounts = [];
    this.accountsError = '';
    this.loadImpersonationAccounts(winery.id);
  }

  private loadImpersonationAccounts(wineryId: string) {
    const privilegedToken = this.adminLoginAsWineryService.getOriginalAdminToken();
    if (!privilegedToken) {
      return;
    }

    this.loadingAccounts = true;
    this.adminLoginAsWineryService.listImpersonationAccounts(wineryId, privilegedToken).subscribe(
      (accounts) => {
        this.impersonationAccounts = accounts || [];
        this.loadingAccounts = false;
        if (this.impersonationAccounts.length === 1) {
          this.selectedAccount = this.impersonationAccounts[0];
        }
      },
      () => {
        this.impersonationAccounts = [];
        this.loadingAccounts = false;
        this.accountsError = 'Errore nel caricamento degli utenti della cantina.';
      }
    );
  }

  onAccountSelected(account: ImpersonationAccountRow) {
    this.selectedAccount = account;
  }

  onAutocompleteOpened() {
    // Adjust overlay width when autocomplete opens
    setTimeout(() => {
      this.adjustOverlayWidth();
    }, 10);
  }

  private filterWineries() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredWineries = [...this.wineries];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredWineries = this.wineries.filter(winery =>
        (winery.name?.toLowerCase().includes(searchLower) || false) ||
        (winery.id?.toLowerCase().includes(searchLower) || false)
      );
    }
  }

  copyWineryId(winery: DWS.Winery, event: Event) {
    event.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(winery.id).then(() => {
        // Mostra messaggio di conferma
        toast(`ID cantina "${winery.name}" copiato negli appunti!`);
      }).catch(() => {
        // Fallback se clipboard non funziona
        toast('Errore durante la copia dell\'ID cantina');
      });
    } else {
      // Fallback per browser che non supportano clipboard API
      toast('Browser non supporta la copia automatica');
    }
  }

  loginAsWineryClicked() {
    if (!this.selectedWinery) {
      console.error('No winery selected');
      return;
    }

    if (this.impersonationAccounts.length > 1 && !this.selectedAccount) {
      toast(tr('Select the user account to access this winery.'));
      return;
    }

    const privilegedToken = this.adminLoginAsWineryService.getOriginalAdminToken();
    const tokenToUse = privilegedToken ? privilegedToken.replace(/^Bearer\s+/i, '') : undefined;
    const accountEmail = this.selectedAccount?.email;

    observe(this.adminLoginAsWineryService.loginAs(this.selectedWinery.id, tokenToUse, accountEmail))
      .subscribe(
        async login => {
          // Close sidenav content before navigation
          this.sidenavContentService.closeContent();

          await this.accountsService.use(login);
          await this.adminAccountService.logout();
          await this.accountsService.use(login);
          await this.wineryWineriesUtilService.retriveWineryWineries(true);
          await new Promise(resolve => setTimeout(resolve, 100));
          window.location.href = this.landingRoute.path();
        },
          (e) => {
            console.error(e);
            if (e.type == 'notmigrated') {
              confirm.yes(`${tr('To access the')} ${this.selectedWinery.name} ${tr(', the winery must first reset its Wine Suite password. Contact the winery to update the password.')}`)
            }
          }
      )
  }

  async openContactImportAdmin(){
    await this.router.navigate(['/admin', 'contact-import-jobs'])
  }

  async openHomepageSetting(){
    await this.router.navigate(['/admin', 'homepage-settings'])
  }

  async openTemplatesBase(){
    await this.router.navigate(['/admin', 'transactional-emails'])
  }

  async openReservationLogs(){
    await this.router.navigate(['/admin', 'reservation-logs'])
  }

  async openNotificationManager(){
    await this.router.navigate(['/admin', 'notifications-manager-standalone'])
  }

  async openNotificationManagerOld(){
    await this.router.navigate(['/admin', 'notifications-manager'])
  }

  async openAvailabilitiesChecker(){
    await this.router.navigate(['/admin', 'availabilities-checker'])
  }

  async openResendEmails() {
    await this.router.navigate(['/admin', 'resend-email'])
  }

  async openBillingTool() {
    await this.router.navigate(['/admin/billing', 'billing-tool'])
  }

  async openSignitOnboarding() {
    await this.router.navigate(['/admin', 'signit-onboarding'])
  }

}
